import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
  robots?: string;
}

const useSEO = ({
  title = "ISTMECH - Institut Supérieur de Technologie Médicale",
  description = "ISTMECH forme des technologistes médicaux en Haïti depuis 1984. Formation de 3 ans en technologie, biologie médicale et analyses cliniques au Cap-Haïtien.",
  keywords = "ISTMECH, technologie médicale, formation médicale, Cap-Haïtien, Haïti, biologie médicale, analyses cliniques, technologiste médical, école médicale",
  image = "https://www.istmech.edu.ht/og-image.png",
  url = "https://www.istmech.edu.ht/",
  type = "website",
  structuredData,
  robots = "index, follow"
}: SEOProps) => {
  useEffect(() => {
    const siteTitle = title.includes('ISTMECH') ? title : `${title} | ISTMECH`;

    // Update title
    document.title = siteTitle;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Open Graph
    updateMetaTag('og:title', siteTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);

    // Twitter
    updateMetaTag('twitter:title', siteTitle, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', image, true);

    // Robots
    updateMetaTag('robots', robots);

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Add structured data
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo="true"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, image, url, type, structuredData, robots]);
};

export default useSEO;
